import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History } from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import {
  listMfnTriggersWithStale,
  listMfnEvaluations,
  RESOLVED_STATUSES,
  TRIGGER_STATUS_LABELS,
  TRIGGER_SOURCE_LABELS,
  type MfnTriggerWithStale,
} from "@/lib/api/mfn";
import { listFunds } from "@/lib/api/funds";
import { listLps } from "@/lib/api/lps";
import { enforceMfnForFund } from "@/lib/pipeline/continuous-mfn";
import { useRole } from "@/lib/auth/use-role";
import { useAuth } from "@/lib/auth/auth-provider";
import { CATEGORY_LABELS } from "@argus/engine";
import { Badge, Card, CardHeader, Dot } from "@/components/ui/primitives";
import { relativeTime, fmtDateTime } from "@/lib/format";

function statusStyle(t: MfnTriggerWithStale): string {
  if (t.status === "open") return "bg-red-950 text-red-300";
  if (RESOLVED_STATUSES.includes(t.status)) return "bg-emerald-950 text-emerald-300";
  return "bg-amber-950 text-amber-300";
}

/** Compact "Continuous enforcement" panel: last run + why + counts + a short
 * history. Fail-soft — `evaluations` is [] both when nothing has run yet and
 * when the migration hasn't landed, so the empty state covers both. */
function ContinuousEnforcementPanel({ tenantId }: { tenantId: string }) {
  const { data: evaluations } = useQuery({
    queryKey: ["mfn-evaluations", tenantId],
    queryFn: () => listMfnEvaluations(tenantId, { limit: 8 }),
  });
  const { data: funds } = useQuery({
    queryKey: ["funds", tenantId],
    queryFn: () => listFunds(tenantId),
  });
  const fundName = (id: string): string => funds?.find((f) => f.id === id)?.name ?? "—";

  const latest = evaluations?.[0];

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            <History className="size-3.5" /> Continuous enforcement
          </span>
        }
      />
      <div className="px-4 pb-4">
        {!evaluations || evaluations.length === 0 ? (
          <p className="max-w-md text-xs text-neutral-500">
            No evaluations recorded yet — runs automatically when provisions, commitments, or
            clauses change.
          </p>
        ) : (
          <>
            {latest ? (
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-neutral-400">
                <span className="text-neutral-200">
                  Last evaluated {relativeTime(latest.ran_at)}
                </span>
                <span className="text-neutral-700">·</span>
                <Badge tone="info">{TRIGGER_SOURCE_LABELS[latest.trigger_source]}</Badge>
                <span className="text-neutral-700">·</span>
                <span>
                  {latest.computed_triggers} computed, {latest.new_triggers} new
                  {latest.newly_stale > 0 ? `, ${latest.newly_stale} flagged stale` : ""}
                </span>
              </div>
            ) : null}
            <ul className="divide-y divide-neutral-800/70 text-xs">
              {evaluations.map((e) => (
                <li key={e.id} className="flex items-center gap-3 py-1.5">
                  <span
                    className="w-20 shrink-0 tabular-nums text-neutral-500"
                    title={fmtDateTime(e.ran_at)}
                  >
                    {relativeTime(e.ran_at)}
                  </span>
                  <span className="w-40 shrink-0 text-neutral-300">
                    {TRIGGER_SOURCE_LABELS[e.trigger_source]}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-neutral-500">
                    {fundName(e.fund_id)}
                  </span>
                  <span className="shrink-0 tabular-nums text-neutral-500">
                    {e.new_triggers > 0 ? `+${e.new_triggers} new` : "no change"}
                    {e.newly_stale > 0 ? ` · ${e.newly_stale} stale` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Card>
  );
}

function MfnListInner() {
  const tenant = useTenant();
  const { user } = useAuth();
  const { canWrite, role } = useRole(tenant.id);
  const canReview = canWrite || role === "reviewer";
  const qc = useQueryClient();
  const [showResolved, setShowResolved] = useState(false);
  const [evalFundId, setEvalFundId] = useState("");
  const [evalResult, setEvalResult] = useState<string | null>(null);
  const [evalFailed, setEvalFailed] = useState(false);

  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });
  const { data: lps } = useQuery({
    queryKey: ["lps", tenant.id],
    queryFn: () => listLps(tenant.id),
  });
  const {
    data: triggers,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["mfn-triggers", tenant.id, showResolved],
    queryFn: () => listMfnTriggersWithStale(tenant.id, { openOnly: !showResolved }),
  });

  const evaluate = useMutation({
    mutationFn: (fundId: string) =>
      enforceMfnForFund({ tenantId: tenant.id, fundId, source: "manual", userId: user?.id }),
    onSuccess: (s) => {
      setEvalFailed(!s);
      if (!s) {
        setEvalResult("MFN evaluation could not complete — check your connection and try again.");
        return;
      }
      const extra = [
        s.newlyStale > 0 ? `${s.newlyStale} flagged stale` : null,
        s.revived > 0 ? `${s.revived} revived` : null,
      ]
        .filter(Boolean)
        .join(", ");
      setEvalResult(
        `Evaluated ${s.clauseCount} MFN clause(s) against ${s.candidateCount} provisions — ${s.computedTriggers} trigger(s) computed, ${s.newTriggers} new${extra ? `, ${extra}` : ""}.`,
      );
      void refetch();
      void qc.invalidateQueries({ queryKey: ["mfn-evaluations", tenant.id] });
    },
    // 2026-07-03 audit fix: enforceMfnForFund can now throw on a genuine
    // core-step failure (permission denied, network, constraint) instead of
    // silently resolving null — surface it here rather than leaving the
    // manual "Run evaluation" button's failure invisible.
    onError: (e: Error) => {
      setEvalFailed(true);
      setEvalResult(`MFN evaluation failed — ${e.message}`);
    },
  });

  const lpName = (id: string): string => lps?.find((l) => l.id === id)?.legal_name ?? "—";
  const fundName = (id: string): string => funds?.find((f) => f.id === id)?.name ?? "—";

  return (
    <>
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">MFN triggers</h1>
          <p className="text-sm text-neutral-400">
            Where one LP&rsquo;s most-favored-nation clause reaches a term another LP received.
          </p>
        </div>
        {canReview ? (
          <div className="flex items-center gap-2">
            <select
              value={evalFundId}
              onChange={(e) => setEvalFundId(e.target.value)}
              className="rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-xs text-neutral-200"
            >
              <option value="">Choose fund…</option>
              {funds?.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!evalFundId || evaluate.isPending}
              onClick={() => evaluate.mutate(evalFundId)}
              className="rounded bg-sky-700 px-3 py-1.5 text-xs font-medium text-sky-50 hover:bg-sky-600 disabled:opacity-50"
            >
              {evaluate.isPending ? "Evaluating…" : "Run MFN evaluation"}
            </button>
          </div>
        ) : null}
      </header>

      <ContinuousEnforcementPanel tenantId={tenant.id} />

      {evalResult ? (
        <div
          className={`rounded border px-3 py-2 text-xs ${
            evalFailed
              ? "border-red-900/60 bg-red-950/30 text-red-300"
              : "border-sky-900/60 bg-sky-950/30 text-sky-300"
          }`}
        >
          {evalResult}
        </div>
      ) : null}

      <label className="flex w-fit items-center gap-2 text-xs text-neutral-400">
        <input
          type="checkbox"
          checked={showResolved}
          onChange={(e) => setShowResolved(e.target.checked)}
          className="accent-neutral-300"
        />
        Show resolved & dismissed
      </label>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading triggers…</p>
      ) : error ? (
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {error.message}
        </div>
      ) : !triggers || triggers.length === 0 ? (
        <div className="rounded border border-neutral-800 bg-neutral-900 p-6 text-center text-sm text-neutral-400">
          <p className="font-medium text-neutral-100">
            No {showResolved ? "" : "open "}MFN triggers
          </p>
          <p className="mt-1">
            Upload side letters, confirm their provisions, then run an MFN evaluation per fund.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-neutral-800">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-neutral-800 bg-neutral-900/50 text-[11px] uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-3 py-2">MFN holder</th>
                <th className="px-3 py-2">Got better term</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Fund</th>
                <th className="px-3 py-2">Confidence</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-900">
              {triggers.map((t) => (
                <tr key={t.id} className="hover:bg-neutral-900/50">
                  <td className="px-3 py-2.5">
                    <Link
                      to="/t/$slug/mfn/$triggerId"
                      params={{ slug: tenant.slug, triggerId: t.id }}
                      className="text-neutral-100 hover:underline"
                    >
                      {lpName(t.holder_lp_id)}
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 text-neutral-300">{lpName(t.beneficiary_lp_id)}</td>
                  <td className="px-3 py-2.5 text-neutral-400">{CATEGORY_LABELS[t.category]}</td>
                  <td className="px-3 py-2.5 text-neutral-400">{fundName(t.fund_id)}</td>
                  <td className="px-3 py-2.5 text-neutral-400">
                    {t.confidence !== null ? `${(Number(t.confidence) * 100).toFixed(0)}%` : "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${statusStyle(t)}`}
                      >
                        {TRIGGER_STATUS_LABELS[t.status]}
                      </span>
                      {t.stale ? (
                        <span title="Stale — no longer computed from current facts; review & dismiss">
                          <Badge tone="ai">
                            <Dot tone="ai" /> Stale
                          </Badge>
                        </span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function MfnListPage() {
  return (
    <TenantLayout>
      <MfnListInner />
    </TenantLayout>
  );
}
